import { FilterDocument, SmartList } from '@griply/types';
import { getFilterDocument } from '@griply/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useUpdateSmartList } from './use-update-smart-list';

export const useSmartListFilterDraft = (smartList: SmartList | undefined) => {
  const { submit: updateSmartList } = useUpdateSmartList();
  const [stagedFilterDocument, setStagedFilterDocument] = useState<
    FilterDocument | undefined
  >();

  const defaultFilterDocument = useMemo(
    () => getFilterDocument(smartList),
    [smartList],
  );

  const activeFilterDocument = useMemo(
    () =>
      smartList ? stagedFilterDocument ?? defaultFilterDocument : undefined,
    [defaultFilterDocument, smartList, stagedFilterDocument],
  );

  const reset = useCallback(() => setStagedFilterDocument(undefined), []);

  // Grouping and sorting are view preferences — persist them immediately to the
  // smart list doc, keeping any staged filter edits in sync. Filter edits stay
  // staged until the explicit save.
  const persistViewPreference = useCallback(
    (patch: Partial<FilterDocument>) => {
      if (!smartList) {
        return;
      }

      updateSmartList({
        id: smartList.id,
        filterDocument: { ...smartList.filterDocument, ...patch },
      });
      setStagedFilterDocument((previous) =>
        previous ? { ...previous, ...patch } : previous,
      );
    },
    [smartList, updateSmartList],
  );

  const updateFilterDocument = useCallback(
    (nextFilterDocument: FilterDocument) => {
      if (!activeFilterDocument) {
        return;
      }

      const viewPreferenceChanged =
        nextFilterDocument.groupByMode !== activeFilterDocument.groupByMode ||
        nextFilterDocument.sortingMode !== activeFilterDocument.sortingMode;

      if (!viewPreferenceChanged) {
        setStagedFilterDocument(nextFilterDocument);
        return;
      }

      persistViewPreference({
        groupByMode: nextFilterDocument.groupByMode,
        sortingMode: nextFilterDocument.sortingMode,
      });
    },
    [activeFilterDocument, persistViewPreference],
  );

  const save = useCallback(async () => {
    if (!smartList || !stagedFilterDocument) {
      return;
    }

    await updateSmartList({
      id: smartList.id,
      filterDocument: stagedFilterDocument,
    });
    reset();
  }, [reset, smartList, stagedFilterDocument, updateSmartList]);

  useEffect(() => {
    setStagedFilterDocument(undefined);
  }, [smartList?.id]);

  return {
    defaultFilterDocument,
    activeFilterDocument,
    stagedFilterDocument,
    persistViewPreference,
    updateFilterDocument,
    save,
    reset,
  };
};
